import { create } from 'zustand';
import type { GraphDocument, GraphNodeOffset } from '../../../core/entities/graph-document';
import { createEmptyGraph, inferGraphTimeZone, mergeGraphLayoutWithOffsets, normalizeGraphLayout, normalizeGraphRelationships, removeNodeFromGraphLayout } from '../../../core/entities/graph-document';
import { currentTimeZone, type JourneyNode, type NodeType } from '../../../core/entities/node';
import { canCreateNodeInAccessMode, type AccessMode } from '../../../core/policies/access-gate/preview-node-entitlement';
import {
	canonicalRelationshipPairId,
	normalizeExplicitRelationshipPair,
	type ExplicitRelationshipPairInput,
	validateExplicitRelationshipPair,
} from '../../../core/policies/explicit-relationships';
import { deriveConnections } from '../../../core/policies/auto-connections/derive-connections';
import { DEFAULT_CONNECTION_STRATEGY, type ConnectionStrategy } from '../../../core/ports/preferences-repository';
import { deleteNodeFromGraph, saveNodeToGraph } from '../../../core/services/node-lifecycle-service';
import {
	advanceCameraInertia,
	createGraphCamera,
	panCamera,
	setCameraTarget as setGraphCameraTarget,
	setCameraVelocity,
	type GraphCamera,
} from './graph-camera';
import {
	advanceNodeMotion,
	clearNodeVelocity,
	setNodeVelocity as setGraphNodeVelocity,
	type GraphNodeVelocityMap,
} from './graph-node-motion';

interface GraphState {
	graph: GraphDocument;
	connectionStrategy: ConnectionStrategy;
	camera: GraphCamera;
	nodeMotionOffsets: Record<string, GraphNodeOffset>;
	nodeOffsets: Record<string, GraphNodeOffset>;
	nodeVelocities: GraphNodeVelocityMap;
	focusedNodeId: string | null;
	visibleType: NodeType | 'All';
	readonlyMode: boolean;
	accessMode: AccessMode;
	mutationVersion: number;
	saveNode(node: JourneyNode): void;
	deleteNode(nodeId: string): void;
	saveRelationshipPair(input: ExplicitRelationshipPairInput): void;
	deleteRelationshipPair(firstNodeId: string, secondNodeId: string): void;
	setGraphTitle(title: string): void;
	setConnectionStrategy(connectionStrategy: ConnectionStrategy): void;
	setReadonlyMode(readonlyMode: boolean): void;
	setAccessMode(accessMode: AccessMode): void;
	focus(nodeId: string | null): void;
	setVisibleType(type: NodeType | 'All'): void;
	replaceGraph(graph: GraphDocument): void;
	panCameraBy(deltaX: number, deltaY: number): void;
	setCameraVelocity(velocityX: number, velocityY: number): void;
	setCameraTarget(targetX: number, targetY: number, targetScale?: number): void;
	advanceCameraInertia(): void;
	resetCameraVelocity(): void;
	moveNodeBy(nodeId: string, deltaX: number, deltaY: number, deltaZ?: number): void;
	commitNodeOffset(nodeId: string): void;
	settleNodeMotion(nodeId: string): void;
	setNodeVelocity(nodeId: string, velocityX: number, velocityY: number): void;
	resetNodeVelocity(nodeId: string): void;
	advanceNodeMotion(): void;
}

export const useGraphStore = create<GraphState>((set) => ({
	graph: createEmptyGraph('JourneyNodes Demo'),
	connectionStrategy: DEFAULT_CONNECTION_STRATEGY,
	camera: createGraphCamera(),
	nodeMotionOffsets: {},
	nodeOffsets: {},
	nodeVelocities: {},
	focusedNodeId: null,
	visibleType: 'All',
	readonlyMode: false,
	accessMode: 'unlocked',
	mutationVersion: 0,
	saveNode: (node) =>
		set((state) => {
			if (state.readonlyMode) return state;
			const isNewNode = !state.graph.nodes.some((item) => item.nodeId === node.nodeId);
			if (isNewNode && !canCreateNodeInAccessMode(state.accessMode, state.graph.nodes.length)) return state;
			return {
				graph: saveNodeToGraph(state.graph, node, { connectionStrategy: state.connectionStrategy }),
				mutationVersion: state.mutationVersion + 1,
			};
		}),
	deleteNode: (nodeId) =>
		set((state) => {
			if (state.readonlyMode) return state;
			if (!state.graph.nodes.some((node) => node.nodeId === nodeId)) return state;
			const nextNodeOffsets = { ...state.nodeOffsets };
			const nextNodeMotionOffsets = { ...state.nodeMotionOffsets };
			delete nextNodeOffsets[nodeId];
			delete nextNodeMotionOffsets[nodeId];

			return {
				graph: {
					...deleteNodeFromGraph(state.graph, nodeId, { connectionStrategy: state.connectionStrategy }),
					layout: removeNodeFromGraphLayout(state.graph.layout, nodeId),
				},
				nodeOffsets: nextNodeOffsets,
				nodeMotionOffsets: nextNodeMotionOffsets,
				nodeVelocities: clearNodeVelocity(state.nodeVelocities, nodeId),
				focusedNodeId: state.focusedNodeId === nodeId ? null : state.focusedNodeId,
				mutationVersion: state.mutationVersion + 1,
			};
		}),
	saveRelationshipPair: (input) =>
		set((state) => {
			if (state.readonlyMode) return state;

			const normalized = normalizeExplicitRelationshipPair(input);
			const validation = validateExplicitRelationshipPair(normalized, state.graph.nodes.map((node) => node.nodeId));
			if (!validation.valid) return state;

			const existingRelationships = state.graph.relationships ?? [];
			const existingPair = existingRelationships.find((pair) => pair.pairId === normalized.pairId);
			const nextPair = {
				...normalized,
				createdAt: existingPair?.createdAt ?? normalized.createdAt,
				createdAtTimeZone: existingPair?.createdAtTimeZone ?? normalized.createdAtTimeZone,
				updatedAt: normalized.updatedAt,
				updatedAtTimeZone: normalized.updatedAtTimeZone,
			};
			const now = new Date().toISOString();
			const timeZone = currentTimeZone();

			return {
				graph: {
					...state.graph,
					relationships: [...existingRelationships.filter((pair) => pair.pairId !== normalized.pairId), nextPair],
					updatedAt: now,
					updatedAtTimeZone: timeZone,
				},
				mutationVersion: state.mutationVersion + 1,
			};
		}),
	deleteRelationshipPair: (firstNodeId, secondNodeId) =>
		set((state) => {
			if (state.readonlyMode) return state;
			const pairId = canonicalRelationshipPairId(firstNodeId, secondNodeId);
			const existingRelationships = state.graph.relationships ?? [];
			if (!existingRelationships.some((pair) => pair.pairId === pairId)) {
				return state;
			}

			const now = new Date().toISOString();
			const timeZone = currentTimeZone();

			return {
				graph: {
					...state.graph,
					relationships: existingRelationships.filter((pair) => pair.pairId !== pairId),
					updatedAt: now,
					updatedAtTimeZone: timeZone,
				},
				mutationVersion: state.mutationVersion + 1,
			};
		}),
	setGraphTitle: (title) =>
		set((state) => {
			if (state.readonlyMode) return state;
			const timeZone = currentTimeZone();
			return {
				graph: {
					...state.graph,
					timestampOriginTimeZone: state.graph.timestampOriginTimeZone ?? inferGraphTimeZone(state.graph),
					createdAtTimeZone: state.graph.createdAtTimeZone ?? inferGraphTimeZone(state.graph),
					title,
					updatedAt: new Date().toISOString(),
					updatedAtTimeZone: timeZone,
				},
				mutationVersion: state.mutationVersion + 1,
			};
		}),
	setConnectionStrategy: (connectionStrategy) =>
		set((state) => {
			const connectionTimeZone = state.graph.updatedAtTimeZone ?? inferGraphTimeZone(state.graph) ?? 'UTC';
			return {
				connectionStrategy,
				graph: {
					...state.graph,
					connections: deriveConnections(state.graph.nodes, state.graph.updatedAt, { strategy: connectionStrategy, timeZone: connectionTimeZone }),
				},
			};
		}),
	setReadonlyMode: (readonlyMode) =>
		set((state) => ({
			readonlyMode,
			nodeOffsets: readonlyMode ? {} : state.nodeOffsets,
			nodeMotionOffsets: readonlyMode ? {} : state.nodeMotionOffsets,
			nodeVelocities: readonlyMode ? {} : state.nodeVelocities,
		})),
	setAccessMode: (accessMode) => set({ accessMode }),
	focus: (focusedNodeId) => set({ focusedNodeId }),
	setVisibleType: (visibleType) => set({ visibleType }),
	replaceGraph: (graph) =>
		set((state) => {
			const fallbackTimeZone = inferGraphTimeZone(graph);
			const nodes = graph.nodes.map((node) => ({
				...node,
				createdAtTimeZone: node.createdAtTimeZone ?? graph.timestampOriginTimeZone ?? graph.createdAtTimeZone,
				updatedAtTimeZone: node.updatedAtTimeZone ?? graph.timestampOriginTimeZone ?? graph.updatedAtTimeZone ?? graph.createdAtTimeZone,
			}));

			return {
				graph: {
					...graph,
					timestampOriginTimeZone: graph.timestampOriginTimeZone ?? fallbackTimeZone,
					createdAtTimeZone: graph.createdAtTimeZone ?? fallbackTimeZone,
					updatedAtTimeZone: graph.updatedAtTimeZone ?? fallbackTimeZone,
					nodes,
					relationships: normalizeGraphRelationships(graph.relationships, nodes),
					layout: normalizeGraphLayout(graph.layout),
					connections: deriveConnections(nodes, graph.updatedAt, {
						strategy: state.connectionStrategy,
						timeZone: graph.updatedAtTimeZone ?? fallbackTimeZone ?? 'UTC',
					}),
				},
				nodeOffsets: {},
				nodeMotionOffsets: {},
				nodeVelocities: {},
				camera: createGraphCamera(),
				focusedNodeId: null,
				mutationVersion: state.mutationVersion + 1,
			};
		}),
	panCameraBy: (deltaX, deltaY) => set((state) => ({ camera: panCamera(state.camera, deltaX, deltaY) })),
	setCameraVelocity: (velocityX, velocityY) =>
		set((state) => ({ camera: setCameraVelocity(state.camera, velocityX, velocityY) })),
	setCameraTarget: (targetX, targetY, targetScale) =>
		set((state) => ({ camera: setGraphCameraTarget(state.camera, targetX, targetY, targetScale) })),
	advanceCameraInertia: () => set((state) => ({ camera: advanceCameraInertia(state.camera) })),
	resetCameraVelocity: () => set((state) => ({ camera: setCameraVelocity(state.camera, 0, 0) })),
	moveNodeBy: (nodeId, deltaX, deltaY, deltaZ = 0) =>
		set((state) => {
			if (state.readonlyMode) return state;

			return {
				nodeOffsets: {
					...state.nodeOffsets,
					[nodeId]: {
						x: (state.nodeOffsets[nodeId]?.x ?? 0) + deltaX,
						y: (state.nodeOffsets[nodeId]?.y ?? 0) + deltaY,
						z: (state.nodeOffsets[nodeId]?.z ?? 0) + deltaZ,
					},
				},
			};
		}),
	commitNodeOffset: (nodeId) =>
		set((state) => {
			if (state.readonlyMode) return state;
			const offset = state.nodeOffsets[nodeId];
			if (!offset) return state;

			const nextNodeOffsets = { ...state.nodeOffsets };
			delete nextNodeOffsets[nodeId];

			return {
				graph: {
					...state.graph,
					layout: mergeGraphLayoutWithOffsets(state.graph.layout, { [nodeId]: offset }),
				},
				nodeOffsets: nextNodeOffsets,
				mutationVersion: state.mutationVersion + 1,
			};
		}),
	settleNodeMotion: (nodeId) =>
		set((state) => {
			if (state.readonlyMode) return state;
			const offsets = {
				...(state.nodeOffsets[nodeId] ? { [nodeId]: state.nodeOffsets[nodeId] } : {}),
				...(state.nodeMotionOffsets[nodeId] ? { [nodeId]: state.nodeMotionOffsets[nodeId] } : {}),
			};

			const nextNodeOffsets = { ...state.nodeOffsets };
			const nextNodeMotionOffsets = { ...state.nodeMotionOffsets };
			delete nextNodeOffsets[nodeId];
			delete nextNodeMotionOffsets[nodeId];

			return {
				graph: {
					...state.graph,
					layout: mergeGraphLayoutWithOffsets(state.graph.layout, offsets),
				},
				nodeOffsets: nextNodeOffsets,
				nodeMotionOffsets: nextNodeMotionOffsets,
				nodeVelocities: clearNodeVelocity(state.nodeVelocities, nodeId),
				mutationVersion: state.mutationVersion + 1,
			};
		}),
	setNodeVelocity: (nodeId, velocityX, velocityY) =>
		set((state) => {
			if (state.readonlyMode) return state;
			return { nodeVelocities: setGraphNodeVelocity(state.nodeVelocities, nodeId, velocityX, velocityY) };
		}),
	resetNodeVelocity: (nodeId) => set((state) => ({ nodeVelocities: clearNodeVelocity(state.nodeVelocities, nodeId) })),
	advanceNodeMotion: () =>
		set((state) => {
			if (state.readonlyMode) {
				return {
					nodeMotionOffsets: {},
					nodeVelocities: {},
				};
			}

			const next = advanceNodeMotion(state.nodeMotionOffsets, state.nodeVelocities);
			const settledOffsets = Object.fromEntries(
				Object.entries(next.offsets).filter(([nodeId]) => !(nodeId in next.velocities))
			);
			const nextNodeMotionOffsets = Object.fromEntries(
				Object.entries(next.offsets).filter(([nodeId]) => nodeId in next.velocities)
			);

			return {
				graph: {
					...state.graph,
					layout: mergeGraphLayoutWithOffsets(state.graph.layout, settledOffsets),
				},
				nodeMotionOffsets: nextNodeMotionOffsets,
				nodeVelocities: next.velocities,
				mutationVersion: state.mutationVersion + 1,
			};
		}),
}));
